// MMEC Brand Identity Colors
// Based on the provided color palette from the brand identity

import type { ColorScheme } from './colorScheme';

export const MMEC_BRAND_COLORS = {
  // Primary Brand Colors
  orange: '#F4A261',    // Energy and creativity
  blue: '#1E40AF',      // Professionalism and trust - darker blue shade
  red: '#D62828',       // Dynamism and attention
  gray: '#A9A9A9',      // Neutral and balanced base
  black: '#000000',     // Contrast and clarity
  white: '#FFFFFF',     // Readability
  
  // Semantic variations
  orangeLight: '#F7B580',   // Lighter orange for hover states
  orangeDark: '#E8944A',    // Darker orange for active states
  blueLight: '#4A8BB8',     // Lighter blue for hover states
  blueDark: '#1E5A7A',      // Darker blue for active states
  redLight: '#E54545',      // Lighter red for hover states
  redDark: '#B81F1F',       // Darker red for active states
  grayLight: '#C4C4C4',     // Lighter gray for hover states
  grayDark: '#8A8A8A',      // Darker gray for active states
};

// MMEC Brand Color Applications (static defaults — charts/widgets read through live proxy below)
const STATIC_MMEC_COLOR_APPLICATIONS = {
  // Primary UI Elements
  primary: MMEC_BRAND_COLORS.blue,
  secondary: MMEC_BRAND_COLORS.orange,
  accent: MMEC_BRAND_COLORS.red,
  neutral: MMEC_BRAND_COLORS.gray,

  // Text Colors
  textPrimary: MMEC_BRAND_COLORS.black,
  textSecondary: MMEC_BRAND_COLORS.gray,
  textInverse: MMEC_BRAND_COLORS.white,

  // Background Colors
  backgroundPrimary: MMEC_BRAND_COLORS.white,
  backgroundSecondary: '#F8F9FA', // Light gray background
  backgroundAccent: MMEC_BRAND_COLORS.blue,

  // Interactive Elements
  buttonPrimary: MMEC_BRAND_COLORS.blue,
  buttonPrimaryHover: MMEC_BRAND_COLORS.blueLight,
  buttonSecondary: MMEC_BRAND_COLORS.orange,
  buttonSecondaryHover: MMEC_BRAND_COLORS.orangeLight,
  buttonDanger: MMEC_BRAND_COLORS.red,
  buttonDangerHover: MMEC_BRAND_COLORS.redLight,

  // Status Colors
  success: '#28A745', // Green for success states
  warning: MMEC_BRAND_COLORS.orange,
  error: MMEC_BRAND_COLORS.red,
  info: MMEC_BRAND_COLORS.blue,

  // Chart Colors
  chartPrimary: MMEC_BRAND_COLORS.blue,
  chartSecondary: MMEC_BRAND_COLORS.orange,
  chartSuccess: '#28A745',
  chartWarning: MMEC_BRAND_COLORS.orange,
  chartError: MMEC_BRAND_COLORS.red,
  chartNeutral: MMEC_BRAND_COLORS.gray,

  // Options Trading Colors
  boughtCalls: '#28A745', // Green for bought calls
  boughtPuts: MMEC_BRAND_COLORS.red, // Red for bought puts
  soldCalls: '#8A2BE2', // Purple for sold calls
  soldPuts: MMEC_BRAND_COLORS.orange, // Orange for sold puts

  // Border Colors
  borderPrimary: MMEC_BRAND_COLORS.gray,
  borderAccent: MMEC_BRAND_COLORS.blue,
  borderFocus: MMEC_BRAND_COLORS.orange,
};

declare global {
  interface Window {
    __MMEC_APP_COLORS__?: Partial<Record<keyof typeof STATIC_MMEC_COLOR_APPLICATIONS, string>>;
  }
}

/** Maps merged theme ColorScheme into chart/widget application colors (UI concepts + light/dark). */
export function syncLiveApplicationsFromColorScheme(colors: ColorScheme): void {
  if (typeof window === 'undefined') return;
  window.__MMEC_APP_COLORS__ = {
    primary: colors.primary,
    secondary: colors.secondary,
    accent: colors.accent,
    neutral: colors.textMuted,
    textPrimary: colors.text,
    textSecondary: colors.textSecondary,
    textInverse: colors.textInverse,
    backgroundPrimary: colors.background,
    backgroundSecondary: colors.surface,
    backgroundAccent: colors.accent,
    buttonPrimary: colors.primary,
    buttonPrimaryHover: colors.primary,
    buttonSecondary: colors.secondary,
    buttonSecondaryHover: colors.secondary,
    buttonDanger: colors.error,
    buttonDangerHover: colors.error,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    chartPrimary: colors.chartPrimary,
    chartSecondary: colors.chartSecondary,
    chartSuccess: colors.chartSuccess,
    chartWarning: colors.chartWarning,
    chartError: colors.chartError,
    chartNeutral: colors.sentimentNeutral,
    boughtCalls: colors.boughtCalls,
    boughtPuts: colors.boughtPuts,
    soldCalls: colors.soldCalls,
    soldPuts: colors.soldPuts,
    borderPrimary: colors.surfaceBorder,
    borderAccent: colors.accent,
    borderFocus: colors.warning,
  };
}

export function clearLiveApplicationsOverride(): void {
  if (typeof window !== 'undefined') {
    delete window.__MMEC_APP_COLORS__;
  }
}

/** Live object: reads merged palette from ThemeProvider when set; otherwise static brand defaults. */
export const MMEC_COLOR_APPLICATIONS = new Proxy(STATIC_MMEC_COLOR_APPLICATIONS, {
  get(target, prop: string) {
    if (typeof window !== 'undefined' && window.__MMEC_APP_COLORS__) {
      const v = window.__MMEC_APP_COLORS__[prop as keyof typeof STATIC_MMEC_COLOR_APPLICATIONS];
      if (v !== undefined) return v;
    }
    return target[prop as keyof typeof STATIC_MMEC_COLOR_APPLICATIONS];
  },
}) as typeof STATIC_MMEC_COLOR_APPLICATIONS;

// Utility functions for applying MMEC brand colors
export const getMMECColor = (colorKey: keyof typeof MMEC_BRAND_COLORS): string => {
  return MMEC_BRAND_COLORS[colorKey];
};

export const getMMECApplicationColor = (applicationKey: keyof typeof MMEC_COLOR_APPLICATIONS): string => {
  return MMEC_COLOR_APPLICATIONS[applicationKey];
};

// Tailwind CSS classes for MMEC brand colors
export const MMEC_TAILWIND_CLASSES = {
  // Background classes
  bgPrimary: 'bg-[#1E40AF]',
  bgSecondary: 'bg-[#F4A261]',
  bgAccent: 'bg-[#D62828]',
  bgNeutral: 'bg-[#A9A9A9]',
  
  // Text classes
  textPrimary: 'text-[#1E40AF]',
  textSecondary: 'text-[#F4A261]',
  textAccent: 'text-[#D62828]',
  textNeutral: 'text-[#A9A9A9]',
  textBlack: 'text-[#000000]',
  textWhite: 'text-[#FFFFFF]',
  
  // Border classes
  borderPrimary: 'border-[#1E40AF]',
  borderSecondary: 'border-[#F4A261]',
  borderAccent: 'border-[#D62828]',
  borderNeutral: 'border-[#A9A9A9]',
  
  // Hover classes
  hoverBgPrimary: 'hover:bg-[#3B82F6]',
  hoverBgSecondary: 'hover:bg-[#F7B580]',
  hoverBgAccent: 'hover:bg-[#E54545]',
  
  // Focus classes
  focusRingPrimary: 'focus:ring-[#1E40AF]',
  focusRingSecondary: 'focus:ring-[#F4A261]',
  focusRingAccent: 'focus:ring-[#D62828]',
};

// Dark/Light Theme Configurations
export const MMEC_LIGHT_THEME = {
  background: MMEC_BRAND_COLORS.white,
  backgroundSecondary: '#F8F9FA',
  backgroundCard: MMEC_BRAND_COLORS.white,
  text: MMEC_BRAND_COLORS.black,
  textSecondary: MMEC_BRAND_COLORS.gray,
  border: '#E9ECEF',
  shadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
};

export const MMEC_DARK_THEME = {
  background: '#000000',  // Pure black background
  backgroundSecondary: '#1A1A1A',
  backgroundCard: '#1A1A1A',
  text: MMEC_BRAND_COLORS.white,
  textSecondary: '#CCCCCC',
  border: '#333333',
  shadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
};

// MMEC Brand Theme Configuration
export const MMEC_BRAND_THEME = {
  colors: MMEC_BRAND_COLORS,
  applications: MMEC_COLOR_APPLICATIONS,
  tailwind: MMEC_TAILWIND_CLASSES,
  light: MMEC_LIGHT_THEME,
  dark: MMEC_DARK_THEME,
  
  // Typography
  fontFamily: {
    primary: 'Inter, system-ui, sans-serif',
    secondary: 'Roboto, system-ui, sans-serif',
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },
  
  // Border Radius
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  },
};

export default MMEC_BRAND_THEME; 